import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, FormControl, TableContainer, Switch, Table, TableHead, TableRow, TableCell, TableBody, Paper, Checkbox, SelectChangeEvent, Box, styled, CircularProgress, Select, InputLabel, MenuItem, TextField, Grid, LinearProgress, FormControlLabel} from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import Stack from '@mui/material/Stack';
import Item from '@mui/material/Stack';
import './IntersectingSeismicsList.css';
import { FormContainer, SelectElement } from 'react-hook-form-mui';
import { StringLiteral } from 'typescript';
import { SubmitHandler, useForm } from 'react-hook-form';
import { WKT } from 'ol/format';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';

import { SeismicFormSelect, Option } from '../../utils/seismicFormSelect';
import { Seismic3DData } from '../../../models/classes/Seismic3DData';
import { useGetFilteredProducts, useGetFilterReferenceValues, useGetSeismicsDataByGeom } from '../../../api/useSeismic3DInfoController';
import { SeismicSearchDTO } from 'features/seismic-3d/models/types/SeismicsSearchDTO';
import { GeopostCheckbox } from 'components/geopostCheckbox/GeopostCheckbox';
import { FeatureGeom } from 'features/seismic-3d/models/classes/FeatureGeom';
import { use3DViewerStore } from 'features/seismic-3d/stores/use3DViewerStore';
import { use3DGridStore } from 'features/seismic-3d/stores/use3DGridStore';
import { SeismicsList } from '../SeismicsList';
import { use3DSceneStore } from 'features/seismic-3d/stores/use3DSceneStore';
import { AmplitudeDomain } from 'features/seismic/models/enums/AmplitudeDomain';
import { SurveyType } from 'features/seismic/models/enums/SurveyType';
import { ProductFilterDTO } from 'features/seismic-3d/models/types/ProductFilterDTO';
import { getParamsFromUrl } from 'features/seismic/utils/seismicUrlUtils';
import { useMetadata3DViewer } from 'features/seismic-3d/api/useVolume';

type Props = {
    geomWkt : string;
    geomSrid: string;
};

export function IntersectingSeismicsList({geomWkt, geomSrid }: Props){

    //const [seismicsData, setSeismicsData] = useState<Seismic3DData[]>();
    const formContext = useForm<ProductFilterDTO>({
        defaultValues: {
            productType: null,
            samplesPerTrace: null,
            volumeDomain: null,
            volumeType: null,
            sampleInterval: null,
        }
    });
    const { handleSubmit, reset: resetFilters } = formContext;

    const [ showSelectedProductsOnly, setShowSelectedProductsOnly ] = useState(false);

    const { current: urlParams } = useRef(getParamsFromUrl());

    const [seismicsData, setSeismicsData] = useState<Seismic3DData[]>();
    const [isTableLoading, setIsTableLoading] = useState<boolean>(false);

    const geopostScene = use3DSceneStore(state => state.scene);
    const selectedSeismics = use3DViewerStore(state => state.selectedVolumeShapes);
    const addSelectedSeismicStore = use3DViewerStore(state => state.addSelectedVolumeShape);
    const removeSelectedSeismicStore = use3DViewerStore(state => state.removeSelectedVolumeShape);
    const addSelectedVolume = use3DGridStore(state => state.addSelectedVolume);
    const removeSelectedVolume = use3DGridStore(state => state.removeSelectedVolume);
    const selectedVolumes = use3DGridStore(state => state.selectedVolumes);

    const { data: filterReferenceValues } = useGetFilterReferenceValues(geomWkt, geomSrid);
    const { mutate: getFilteredProducts, isLoading } = useGetFilteredProducts();

    const { mutateAsync: getVolumeMetadata } = useMetadata3DViewer();

    const [ productNameFilter, setProductNameFilter ] = useState('');

    const [loadingSeismicTokens, setLoadingSeismicTokens] = useState<string[]>([]);

    const addSelectedSeismic = async (seismic3DData: Seismic3DData, seismicGeom: FeatureGeom) => {
        addSelectedSeismicStore(seismic3DData);
        geopostScene?.addSeismicMesh(seismic3DData.volumeToken, seismicGeom);
    };

    const removeSelectedSeismic = async (seismic3DData: Seismic3DData) => {
        removeSelectedSeismicStore(seismic3DData);
        await geopostScene?.removeSeismicMesh(seismic3DData.volumeToken);
    };

    const handleGetProducts = useCallback<SubmitHandler<ProductFilterDTO>>((filters, evt) => {
        getFilteredProducts({...filters, geomWkt, geomSrid: parseInt(geomSrid)}, {
            onSuccess: (products) => {
                setSeismicsData(products);
            }
        });
    }, [geomWkt, geomSrid, getFilteredProducts]);

    const handleGridToggle = useCallback((product: Seismic3DData, checked: boolean) => {
        if (checked) {
            getVolumeMetadata({
                volumeIdentifier: urlParams.volumeIdentifier,
                volumeToken: product.volumeToken,
                screenHeight: window.screen.availHeight,
                tileWidth: 100,
                factorDivide: 1,
                factorToChangeDivide: 1,
                includePercentageCalc: false,
                skipInline: 0,
                skipXLine: 0,
            }).then((metadataResponse) => addSelectedVolume(metadataResponse.Metadata))
                .catch((error) => {});
        }
        else {
            const volume = selectedVolumes.find(vol => vol.VolumeToken === product.volumeToken);
            if (!!volume) {
                removeSelectedVolume(volume);
            }
        }

    }, [addSelectedVolume, getVolumeMetadata, removeSelectedVolume, selectedVolumes, urlParams.volumeIdentifier]);

    const filteredProducts = useMemo(() => (seismicsData ?? []).filter(product => {
        let match = false;
        if (productNameFilter === '' || product.productName.toLowerCase().includes(productNameFilter.toLowerCase())) {
            match = true;
        }
        if (showSelectedProductsOnly && !selectedVolumes.find(vol => vol.VolumeToken.toString() === product.volumeToken.toString())) {
            match = false;
        }
        return match;
    }), [seismicsData, productNameFilter, showSelectedProductsOnly, selectedVolumes]);

    return (
        <>
            {isLoading && <LinearProgress/>}
            <Box padding={'5px 5px'}>
                <Stack padding={1} direction="row" justifyContent={'space-between'}>
                    <FormContainer<ProductFilterDTO> formContext={formContext} handleSubmit={handleSubmit(handleGetProducts)} FormProps={{style:{width: '100%'}}}>
                        <Grid container spacing={2}>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    size='small'
                                    label='Product name'
                                    value={productNameFilter}
                                    onChange={(evt) => setProductNameFilter(evt.target.value)}
                                    InputProps={{
                                        endAdornment: <InputAdornment position='end'><SearchIcon></SearchIcon></InputAdornment>
                                    }}
                                />
                            </Grid>
                            <Grid item xs={1}>
                                <SelectElement
                                    fullWidth
                                    size='small'
                                    label='Type'
                                    name='volumeType'
                                    options={[
                                        {
                                            id: SurveyType.Seismic2D,
                                            label: '2D'
                                        },
                                        {
                                            id: SurveyType.Seismic3D,
                                            label: '3D'
                                        },
                                    ]}
                                >
                                </SelectElement>
                            </Grid>
                            <Grid item xs={2}>
                                <SelectElement
                                    fullWidth
                                    size='small'
                                    label="Domain"
                                    name="volumeDomain"
                                    options={[
                                        {
                                            id: AmplitudeDomain.Depth,
                                            label: 'Depth'
                                        },
                                        {
                                            id: AmplitudeDomain.Time,
                                            label: 'Time'
                                        }
                                    ]}
                                >
                                </SelectElement>
                            </Grid>
                            <Grid item xs={2}>
                                <SelectElement
                                    fullWidth
                                    size='small'
                                    label="Product type"
                                    name="productType"
                                    options={filterReferenceValues?.ProductTypeValues.map(value => ({
                                        id: value,
                                        label: value,
                                    }))}
                                >
                                </SelectElement>
                            </Grid>
                            <Grid item xs={2}>
                                <SelectElement
                                    fullWidth
                                    size='small'
                                    label="Samples per trace"
                                    name='samplesPerTrace'
                                    options={filterReferenceValues?.SamplesPerTraceValues.map(value => ({
                                        id: value,
                                        label: value,
                                    }))}
                                >
                                </SelectElement>
                            </Grid>
                            <Grid item xs={2}>
                                <SelectElement
                                    fullWidth
                                    size='small'
                                    label="Sample interval"
                                    name='sampleInterval'
                                    options={filterReferenceValues?.SampleIntervalValues.map(value => ({
                                        id: value,
                                        label: value + 'ms',
                                    }))}
                                >
                                </SelectElement>
                            </Grid>
                            <Grid item xs={2}>
                                <Button
                                    size='small'
                                    sx={{width: '100%', height: '100%'}}
                                    disabled={isLoading}
                                    onClick={() => resetFilters()}
                                    variant='contained'
                                >
                                    Clear filters
                                </Button>
                            </Grid>
                            <Grid item xs={1}>
                                <Button
                                    type='submit'
                                    size='small'
                                    sx={{width: '100%', height: '100%'}}
                                    disabled={isLoading}
                                    variant='contained'
                                >
                                    Search
                                </Button>
                            </Grid>
                            <Grid item xs={4}>
                                <FormControlLabel
                                    control={<Switch checked={showSelectedProductsOnly} onChange={() => setShowSelectedProductsOnly(!showSelectedProductsOnly)}/>}
                                    label='Only selected products'
                                />
                            </Grid>
                        </Grid>
                    </FormContainer>
                </Stack>
                {
                    seismicsData
                    &&
                    <SeismicsList
                        onDisplayShape={addSelectedSeismic}
                        onHideShape={removeSelectedSeismic}
                        onToggleGrid={handleGridToggle}
                        seismics={filteredProducts}
                        selectedSeismics={selectedSeismics}
                        isLoadingCallback={(seismicData) => loadingSeismicTokens.includes(seismicData.volumeToken)}
                    />
                }
            </Box>
        </>
    );
}
